import {
    Box,
    Card,
    CardContent,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    Stack,
    Theme,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import * as d3 from 'd3';
import { useEffect, useRef, useState } from 'react';
import {
    OnlyFansMetricUserSalesDetails,
    OnlyFansMetricUserSalesDetailsRiskLevel,
} from '../../../../hooks/useSextforceMetricsUsersSalesDetails';
import { isValidDateString } from '../../../../utils/common';

// Helper type for the graph
type UserForGraph = {
    id: number;
    date: Date;
    userName: string;
    riskLevel: OnlyFansMetricUserSalesDetailsRiskLevel;
};

// Props for the UserScatterChart component
type UserScatterChartProps = {
    users: UserForGraph[];
    hideNonSuspiciousUsers: boolean;
    onlyShowRecentUsers: boolean;
    theme: Theme;
};

const UserScatterPlot = (props: UserScatterChartProps) => {
    const { users, hideNonSuspiciousUsers, onlyShowRecentUsers, theme } = props;
    const svgRef = useRef(null);
    const containerRef = useRef<any>(null);
    const largeScreen = useMediaQuery(theme.breakpoints.up('sm'));
    const [containerWidth, setContainerWidth] = useState(0);
    const [containerHeight, setContainerHeight] = useState(0);
    const [filteredUsersList, setFilteredUsersList] = useState<UserForGraph[]>([]);

    // Color mapping for risk levels
    const getRiskLevelColor = (riskLevel: OnlyFansMetricUserSalesDetailsRiskLevel): string => {
        switch (riskLevel) {
            case OnlyFansMetricUserSalesDetailsRiskLevel.EXTREME:
                return theme.palette.error.dark;
            case OnlyFansMetricUserSalesDetailsRiskLevel.VERY_HIGH:
                return theme.palette.error.main;
            case OnlyFansMetricUserSalesDetailsRiskLevel.HIGH:
                return theme.palette.warning.main;
            case OnlyFansMetricUserSalesDetailsRiskLevel.MODERATE:
                return theme.palette.warning.light;
            case OnlyFansMetricUserSalesDetailsRiskLevel.LOW:
                return theme.palette.info.main;
            default:
                return theme.palette.primary.main;
        }
    };

    // Get radius based on risk level
    const getRiskLevelRadius = (riskLevel: OnlyFansMetricUserSalesDetailsRiskLevel): number => {
        switch (riskLevel) {
            case OnlyFansMetricUserSalesDetailsRiskLevel.EXTREME:
            case OnlyFansMetricUserSalesDetailsRiskLevel.VERY_HIGH:
                return 4;
            case OnlyFansMetricUserSalesDetailsRiskLevel.HIGH:
            case OnlyFansMetricUserSalesDetailsRiskLevel.MODERATE:
                return 3;
            default:
                return 2;
        }
    };

    // Get risk level label
    const getRiskLevelLabel = (riskLevel: OnlyFansMetricUserSalesDetailsRiskLevel): string => {
        switch (riskLevel) {
            case OnlyFansMetricUserSalesDetailsRiskLevel.EXTREME:
                return 'Extreme Risk';
            case OnlyFansMetricUserSalesDetailsRiskLevel.VERY_HIGH:
                return 'Very High Risk';
            case OnlyFansMetricUserSalesDetailsRiskLevel.HIGH:
                return 'High Risk';
            case OnlyFansMetricUserSalesDetailsRiskLevel.MODERATE:
                return 'Moderate Risk';
            case OnlyFansMetricUserSalesDetailsRiskLevel.LOW:
                return 'Low Risk';
            default:
                return 'No Risk';
        }
    };

    useEffect(() => {
        let filteredUsers: UserForGraph[] = users;

        if (hideNonSuspiciousUsers) {
            filteredUsers = filteredUsers.filter(user => user.riskLevel > OnlyFansMetricUserSalesDetailsRiskLevel.NONE);
        }

        if (onlyShowRecentUsers) {
            // Filter users whose user IDs are greater than 300,000,000
            filteredUsers = filteredUsers.filter(user => user.id > 300000000);
        }

        setFilteredUsersList(filteredUsers);
    }, [hideNonSuspiciousUsers, onlyShowRecentUsers, users]);

    // Function to update the container width
    const updateWidth = () => {
        if (containerRef.current) {
            setContainerWidth(containerRef.current.offsetWidth);
            setContainerHeight(containerRef.current.offsetHeight);
        }
    };

    useEffect(() => {
        updateWidth();
        window.addEventListener('resize', updateWidth);

        return () => {
            window.removeEventListener('resize', updateWidth);
        };
    }, []);

    // Clustering function - only cluster suspicious users
    const clusterUsers = (users: UserForGraph[], epsilon: number, minPoints: number) => {
        const clusters: UserForGraph[][] = [];
        const visited = new Set<number>();

        const regionQuery = (point: UserForGraph, epsilon: number) => {
            return users.filter(
                q =>
                    Math.abs(point.id - q.id) <= epsilon &&
                    Math.abs(new Date(point.date).getTime() - new Date(q.date).getTime()) <= epsilon * 24 * 60 * 60 * 1000,
            );
        };

        const expandCluster = (point: UserForGraph, neighbors: UserForGraph[]) => {
            const cluster: UserForGraph[] = [point];
            for (let i = 0; i < neighbors.length; i++) {
                const neighbor = neighbors[i];
                if (!visited.has(neighbor.id)) {
                    visited.add(neighbor.id);
                    const neighborNeighbors = regionQuery(neighbor, epsilon);
                    if (neighborNeighbors.length >= minPoints) {
                        neighbors.push(...neighborNeighbors.filter(n => !visited.has(n.id)));
                    }
                }
                if (!clusters.some(c => c.includes(neighbor))) {
                    cluster.push(neighbor);
                }
            }
            return cluster;
        };

        for (const point of users) {
            if (visited.has(point.id)) continue;
            visited.add(point.id);
            const neighbors = regionQuery(point, epsilon);
            if (neighbors.length < minPoints) continue;
            const cluster = expandCluster(point, neighbors);
            clusters.push(cluster);
        }

        return clusters;
    };

    useEffect(() => {
        if (containerWidth === 0 || !svgRef.current) return;

        const width = containerWidth;
        const height = containerHeight;
        const margin = { top: 0, right: 20, bottom: 20, left: largeScreen ? 60 : 40 };

        // Scales
        // X Scale with 5% padding
        const [minId, maxId] = d3.extent(filteredUsersList, d => d.id) as [number, number];
        const idRange = maxId - minId;
        const xPadding = idRange * 0.05;

        const xScale = d3
            .scaleLinear()
            .domain([minId - xPadding, maxId + xPadding])
            .range([margin.left, width - margin.right]);

        // Y Scale with 5% padding (optional)
        const [minDate, maxDate] = d3.extent(filteredUsersList, d => new Date(d.date)) as [Date, Date];
        const timeRange = (maxDate?.getTime() || 0) - (minDate?.getTime() || 0);
        const yPadding = timeRange * 0.05;

        const yScale = d3
            .scaleTime()
            .domain([new Date((minDate?.getTime() || 0) - yPadding), new Date((maxDate?.getTime() || 0) + yPadding)])
            .range([height - margin.bottom, margin.top]);

        const svg = d3.select(svgRef.current).attr('width', width).attr('height', height);

        svg.selectAll('*').remove();

        // Axes
        svg.append('g')
            .attr('transform', `translate(0,${height - margin.bottom})`)
            .call(d3.axisBottom(xScale).ticks(width / 200))
            .attr('font-size', '13');

        svg.append('g')
            .attr('transform', `translate(${margin.left},0)`)
            .call(d3.axisLeft(yScale).ticks(height / 100))
            .attr('font-size', '13');

        // Grid
        svg.append('g')
            .attr('stroke', 'currentColor')
            .attr('stroke-opacity', 0.1)
            .call(g =>
                g
                    .append('g')
                    .selectAll('line')
                    .data(xScale.ticks())
                    .join('line')
                    .attr('x1', d => 0.5 + xScale(d))
                    .attr('x2', d => 0.5 + xScale(d))
                    .attr('y1', margin.top)
                    .attr('y2', height - margin.bottom),
            )
            .call(g =>
                g
                    .append('g')
                    .selectAll('line')
                    .data(yScale.ticks())
                    .join('line')
                    .attr('y1', d => 0.5 + yScale(d))
                    .attr('y2', d => 0.5 + yScale(d))
                    .attr('x1', margin.left)
                    .attr('x2', width - margin.right),
            );

        // Tooltip
        const tooltip = d3
            .select(containerRef.current)
            .append('div')
            .style('position', 'absolute')
            .style('visibility', 'hidden')
            .style('background-color', 'white')
            .style('border', 'solid')
            .style('border-width', '1px')
            .style('border-radius', `${theme.shape.borderRadius}px`)
            .style('padding', '10px')
            .style('z-index', 1000);

        // Cluster suspicious users (risk level > NONE)
        const suspiciousUsers = filteredUsersList.filter(u => u.riskLevel > OnlyFansMetricUserSalesDetailsRiskLevel.NONE);
        const clusters = clusterUsers(suspiciousUsers, 1000000, 5);

        // Draw rectangles around clusters
        svg.selectAll('.cluster-rect')
            .data(clusters)
            .enter()
            .append('rect')
            .attr('class', 'cluster-rect')
            .attr('x', d => d3.min(d, u => xScale(u.id))! - 5)
            .attr('y', d => d3.min(d, u => yScale(new Date(u.date)))! - 5)
            .attr('width', d => d3.max(d, u => xScale(u.id))! - d3.min(d, u => xScale(u.id))! + 10)
            .attr('height', d => d3.max(d, u => yScale(new Date(u.date)))! - d3.min(d, u => yScale(new Date(u.date)))! + 10)
            .attr('fill', `${theme.palette.error.main}22`)
            .attr('stroke', theme.palette.warning.main)
            .attr('stroke-width', 2)
            .attr('rx', 5)
            .attr('ry', 5);

        // Draw circles
        svg.selectAll('.user')
            .data(filteredUsersList)
            .join('circle')
            .attr('class', 'user')
            .attr('cx', d => xScale(d.id))
            .attr('cy', d => yScale(new Date(d.date)))
            .attr('r', d => getRiskLevelRadius(d.riskLevel))
            .attr('fill', d => getRiskLevelColor(d.riskLevel))
            .attr('opacity', 0.7)
            .on('mouseover', function (event, d) {
                d3.select(this)
                    .attr('r', getRiskLevelRadius(d.riskLevel) + 2)
                    .attr('opacity', 1)
                    .style('stroke', '#000')
                    .style('stroke-width', 1);

                const riskLabel = getRiskLevelLabel(d.riskLevel);
                tooltip
                    .style('visibility', 'visible')
                    .html(
                        `<strong>User ID:</strong> ${d.id}<br/>` +
                            `<strong>Username:</strong> ${d.userName || 'N/A'}<br/>` +
                            `<strong>Subscribed:</strong> ${new Date(d.date).toLocaleDateString()}<br/>` +
                            `<strong>Risk Level:</strong> <span style="color: ${getRiskLevelColor(d.riskLevel)}">${riskLabel}</span>`,
                    )
                    .style('left', `${event.pageX + 10}px`)
                    .style('top', `${event.pageY + 10}px`);
            })
            .on('mousemove', function (event) {
                tooltip.style('top', `${event.pageY + 10}px`).style('left', `${event.pageX + 10}px`);
            })
            .on('mouseout', function (event, d) {
                d3.select(this).attr('r', getRiskLevelRadius(d.riskLevel)).attr('opacity', 0.7).style('stroke', 'none');

                tooltip.style('visibility', 'hidden');
            });

        // Cleanup tooltip on unmount
        return () => {
            tooltip.remove();
        };
    }, [
        containerHeight,
        containerWidth,
        largeScreen,
        theme.palette.error.main,
        theme.palette.primary.main,
        theme.palette.warning.main,
        theme.shape.borderRadius,
        filteredUsersList,
    ]);

    return (
        <Box id="usersIdsScatterGraph" ref={containerRef} sx={{ width: '100%', height: { xs: 300, sm: 400 } }}>
            <svg ref={svgRef} width={containerWidth} height={400}></svg>
        </Box>
    );
};

type Props = {
    users: OnlyFansMetricUserSalesDetails[];
};

const SextforceMetricsUsersIdScatterWithRiskLevelGraph = (props: Props) => {
    const { users } = props;
    const theme: Theme = useTheme();

    const [hideNonSuspiciousUsers, setHideNonSuspiciousUsers] = useState(false);
    const [onlyShowRecentUsers, setOnlyShowRecentUsers] = useState(false);

    // Transform users to graph format and filter out users with invalid dates
    const usersForGraph: UserForGraph[] = users
        .filter(
            user =>
                user.subscribedAt &&
                ((typeof user.subscribedAt === 'string' && isValidDateString(user.subscribedAt)) || user.subscribedAt instanceof Date),
        )
        .map(user => ({
            id: user.userId,
            date: new Date(user.subscribedAt),
            userName: user.userName,
            riskLevel: user.riskLevel,
        }))
        .sort((a, b) => a.id - b.id);

    // Count suspicious users by risk level
    const riskLevelCounts = users.reduce((acc, user) => {
        acc[user.riskLevel] = (acc[user.riskLevel] || 0) + 1;
        return acc;
    }, {} as Record<OnlyFansMetricUserSalesDetailsRiskLevel, number>);

    const suspiciousUsersCount = users.filter(u => u.riskLevel > OnlyFansMetricUserSalesDetailsRiskLevel.NONE).length;
    const totalUsersCount = users.length;
    const suspiciousUsersPercentage = totalUsersCount > 0 ? (suspiciousUsersCount / totalUsersCount) * 100 : 0;
    const suspiciousUsersPercentageRounded = Math.round(suspiciousUsersPercentage * 100) / 100;

    // Create risk level legend
    const getLegendItem = (level: OnlyFansMetricUserSalesDetailsRiskLevel, label: string) => {
        const count = riskLevelCounts[level] || 0;
        if (count === 0) return null;

        return (
            <Stack key={level} direction="row" spacing={1} alignItems="center">
                <Box
                    sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: (() => {
                            switch (level) {
                                case OnlyFansMetricUserSalesDetailsRiskLevel.EXTREME:
                                    return theme.palette.error.dark;
                                case OnlyFansMetricUserSalesDetailsRiskLevel.VERY_HIGH:
                                    return theme.palette.error.main;
                                case OnlyFansMetricUserSalesDetailsRiskLevel.HIGH:
                                    return theme.palette.warning.main;
                                case OnlyFansMetricUserSalesDetailsRiskLevel.MODERATE:
                                    return theme.palette.warning.light;
                                case OnlyFansMetricUserSalesDetailsRiskLevel.LOW:
                                    return theme.palette.info.main;
                                default:
                                    return theme.palette.primary.main;
                            }
                        })(),
                    }}
                />
                <Typography variant="body2">
                    {label}: {count.toLocaleString()}
                </Typography>
            </Stack>
        );
    };

    return (
        <Card sx={{ width: '100%', marginBottom: 2 }}>
            <CardContent>
                <Grid container flexGrow={1} spacing={1} sx={{ marginBottom: 1 }}>
                    <Grid item xs={12}>
                        <Typography variant="h5" gutterBottom>
                            User IDs Distribution & Risk Analysis
                        </Typography>
                        <Typography variant="body1">
                            The following graph shows a distribution of user IDs and their subscription dates with risk-based color coding.
                            When user IDs are close together and show suspicious patterns, they are marked with higher risk levels. Strong
                            groupings are surrounded by{' '}
                            <span
                                style={{
                                    color: theme.palette.warning.main,
                                    backgroundColor: `${theme.palette.warning.main}22`,
                                }}
                            >
                                orange rectangles
                            </span>
                            . A few suspicious users may not be concerning, but a large number with high risk levels is a red flag,
                            especially if they haven't spent money.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ marginTop: 1 }}>
                        {totalUsersCount >= 20000 && (
                            <Typography variant="body1" color={theme.palette.warning.main}>
                                Too many users for meaningful statistical analysis.
                            </Typography>
                        )}
                        {totalUsersCount < 20000 && suspiciousUsersCount > 0 && (
                            <Box>
                                <Typography variant="body1" color={theme.palette.error.main}>
                                    Found {suspiciousUsersCount.toLocaleString()} suspicious users ({suspiciousUsersPercentageRounded}% of
                                    total)
                                </Typography>
                                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ marginTop: 1, marginBottom: 1 }}>
                                    {getLegendItem(OnlyFansMetricUserSalesDetailsRiskLevel.EXTREME, 'Extreme Risk')}
                                    {getLegendItem(OnlyFansMetricUserSalesDetailsRiskLevel.VERY_HIGH, 'Very High Risk')}
                                    {getLegendItem(OnlyFansMetricUserSalesDetailsRiskLevel.HIGH, 'High Risk')}
                                    {getLegendItem(OnlyFansMetricUserSalesDetailsRiskLevel.MODERATE, 'Moderate Risk')}
                                    {getLegendItem(OnlyFansMetricUserSalesDetailsRiskLevel.LOW, 'Low Risk')}
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems={'center'} flexWrap="wrap">
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={hideNonSuspiciousUsers}
                                                    onChange={e => setHideNonSuspiciousUsers(e.target.checked)}
                                                    color="primary"
                                                />
                                            }
                                            label="Hide non-suspicious users"
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={onlyShowRecentUsers}
                                                    onChange={e => setOnlyShowRecentUsers(e.target.checked)}
                                                    color="primary"
                                                />
                                            }
                                            label="Only show recent users"
                                        />
                                    </FormControl>
                                </Stack>
                            </Box>
                        )}

                        {totalUsersCount < 20000 && suspiciousUsersCount === 0 && (
                            <Typography variant="body1" color={theme.palette.success.dark} gutterBottom>
                                No suspicious users detected
                            </Typography>
                        )}
                    </Grid>
                </Grid>
                <Grid container flexGrow={1} spacing={0} sx={{ padding: 0 }}>
                    <Grid item xs={12}>
                        <UserScatterPlot
                            users={usersForGraph}
                            hideNonSuspiciousUsers={hideNonSuspiciousUsers}
                            onlyShowRecentUsers={onlyShowRecentUsers}
                            theme={theme}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default SextforceMetricsUsersIdScatterWithRiskLevelGraph;
